/**
 * Utilitaires de détection de plateforme pour PWA
 *
 * Fournit des fonctions pour détecter le système d'exploitation,
 * le navigateur, et l'état d'installation de l'application PWA.
 *
 * Utilisé principalement pour adapter l'UI du prompt d'installation
 * selon la plateforme de l'utilisateur.
 *
 * @module detectPlatform
 * @author Équipe Brydge
 * @since 1.0.0
 */

/**
 * Détecte si l'application tourne sur iOS (iPhone ou iPad)
 *
 * @return {boolean} true si iOS, false sinon
 */
export const isIOS = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

/**
 * Détecte la version iOS
 *
 * Parse le user agent pour extraire la version majeure d'iOS.
 * Formats supportés : "OS 18_0", "OS 26_0_1", "Version/26.0"
 *
 * @return {number|null} Version iOS ou null si non-iOS
 */
export const getIOSVersion = () => {
  if (!isIOS()) return null;

  const userAgent = window.navigator.userAgent;

  // Debug temporaire
  console.log('[detectPlatform] User Agent:', userAgent);

  // Essayer le format classique "OS XX_X"
  let match = userAgent.match(/OS (\d+)_/i);
  console.log('[detectPlatform] OS match:', match);
  if (match) {
    const version = parseInt(match[1], 10);
    console.log('[detectPlatform] iOS version (OS format):', version);
    return version;
  }

  // Essayer le format "Version/XX.X" (utilisé dans les versions récentes)
  match = userAgent.match(/Version\/(\d+)\./i);
  console.log('[detectPlatform] Version match:', match);
  if (match) {
    const version = parseInt(match[1], 10);
    console.log('[detectPlatform] iOS version (Version format):', version);
    return version;
  }

  console.log('[detectPlatform] Aucune version iOS trouvée');
  return null;
};

/**
 * Détecte si l'application tourne sur Android
 *
 * @return {boolean} true si Android, false sinon
 */
export const isAndroid = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /android/.test(userAgent);
};

/**
 * Détecte si l'application est en mode standalone (installée)
 *
 * Vérifie plusieurs indicateurs selon la plateforme :
 * - Pour iOS : window.navigator.standalone (méthode la plus fiable)
 * - Pour Android/Desktop : window.matchMedia('(display-mode: standalone)')
 * - Pour Android : document.referrer contenant 'android-app://'
 *
 * @return {boolean} true si installée, false sinon
 */
export const isStandalone = () => {
  // iOS Safari - Méthode la plus fiable
  // navigator.standalone est undefined dans Safari normal, true dans PWA installée
  if (isIOS()) {
    return window.navigator.standalone === true;
  }

  // Android et Desktop - matchMedia
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // Android - Référence depuis une app installée
  if (document.referrer.includes('android-app://')) {
    return true;
  }

  return false;
};

/**
 * Détecte si l'application tourne dans Safari (iOS)
 *
 * @return {boolean} true si Safari, false sinon
 */
export const isSafari = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return (
    /safari/.test(userAgent) &&
    !/chrome/.test(userAgent) &&
    !/crios/.test(userAgent) &&
    !/fxios/.test(userAgent)
  );
};

/**
 * Détecte si l'application tourne dans Chrome (Android ou Desktop)
 *
 * @return {boolean} true si Chrome, false sinon
 */
export const isChrome = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /chrome/.test(userAgent) && !/edg/.test(userAgent);
};

/**
 * Retourne la plateforme détectée
 *
 * @return {'ios'|'android'|'desktop'} La plateforme de l'utilisateur
 */
export const detectPlatform = () => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'desktop';
};

/**
 * Vérifie si l'installation PWA est possible sur cette plateforme
 *
 * @return {boolean} true si l'installation est supportée
 */
export const canInstall = () => {
  // Déjà installé
  if (isStandalone()) {
    return false;
  }

  // iOS : uniquement dans Safari
  if (isIOS()) {
    return isSafari();
  }

  // Android : Chrome et autres navigateurs modernes
  if (isAndroid()) {
    return true;
  }

  // Desktop : Chrome, Edge, Opera supportent PWA
  return isChrome();
};

/**
 * Objet contenant toutes les informations de détection
 *
 * @return {Object} Informations complètes sur la plateforme
 * @return {string} returns.platform - Plateforme détectée (ios/android/desktop)
 * @return {boolean} returns.isInstalled - Si l'app est installée
 * @return {boolean} returns.canInstall - Si l'installation est possible
 * @return {boolean} returns.isIOS - Si la plateforme est iOS
 * @return {boolean} returns.isAndroid - Si la plateforme est Android
 * @return {boolean} returns.isSafari - Si le navigateur est Safari
 * @return {boolean} returns.isChrome - Si le navigateur est Chrome
 * @return {number|null} returns.iosVersion - Version iOS ou null si non-iOS
 * @return {string} returns.userAgent - User agent du navigateur
 */
export const getPlatformInfo = () => {
  return {
    platform: detectPlatform(),
    isInstalled: isStandalone(),
    canInstall: canInstall(),
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isSafari: isSafari(),
    isChrome: isChrome(),
    iosVersion: getIOSVersion(),
    userAgent: window.navigator.userAgent,
  };
};
